"use client";

import { AudioFormat, CommitStrategy, RealtimeEvents, Scribe, type RealtimeConnection } from "@elevenlabs/client";
import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { startPcmCapture, type PcmCapture } from "@/lib/dictation/audio-capture";
import { acquireDictationLease, isCurrentDictationLease, releaseDictationLease } from "@/lib/dictation/coordinator";
import { createShortcutState, DICTATION_HOLD_THRESHOLD_MS, reduceShortcut, type ShortcutPlatform, type ShortcutState } from "@/lib/dictation/shortcut-state";
import type { DictationMode } from "@/lib/dictation/settings";
import { loadDictationOrganizer } from "@/lib/dictation/organizer-loader";
import { MAX_DICTATION_SESSION_SECONDS } from "@/lib/dictation/limits";
import { useDictationStore } from "@/stores/dictation-store";
import { startAuthorToolTracking, type AuthorToolTracker } from "@/lib/analytics/author-tools-client";
const FINAL_TRANSCRIPT_TIMEOUT_MS = 8_000;
const BOOTSTRAP_TIMEOUT_MS = 10_000;
const OPEN_TIMEOUT_MS = 8_000;
const ORGANIZE_TIMEOUT_MS = 8_000;
export type DictationStatus = "idle" | "listening" | "processing" | "error";
export type DictationListeningMode = "pending" | "held" | "latched";
export interface LockedDictationTarget {
  insert: (text: string) => (() => void) | void;
  restoreFocus: () => void;
  isValid?: () => boolean;
}
interface Session {
  id: number;
  mode: DictationMode;
  target: LockedDictationTarget;
  chunks: string[];
  transcripts: string[];
  capture: PcmCapture | null;
  connection: RealtimeConnection | null;
  isOpen: boolean;
  isCancelled: boolean;
  isFinalizing: boolean;
  wantsFinish: boolean;
  finalTimer: ReturnType<typeof setTimeout> | null;
  maxTimer: ReturnType<typeof setTimeout> | null;
  bootstrapTimer: ReturnType<typeof setTimeout> | null;
  openTimer: ReturnType<typeof setTimeout> | null;
  abortController: AbortController;
  isTerminal: boolean;
  isProviderConfirmed: boolean;
  providerReady: Promise<void>;
  confirmProvider: () => void;
  tracking: AuthorToolTracker;
}
interface PointerGesture {
  pointerId: number;
  sessionId: number | null;
  wasListeningOnDown: boolean;
  startedAt: number;
  startX: number;
  startY: number;
  isDragging: boolean;
}
const POINTER_DRAG_THRESHOLD_PX = 8;
function detectPlatform(): ShortcutPlatform {
  const platform = (navigator as Navigator & {
    userAgentData?: {
      platform?: string;
    };
  }).userAgentData?.platform ?? navigator.platform ?? "";
  if (/mac/i.test(platform)) return "mac";
  if (/win/i.test(platform)) return "windows";
  return "other";
}
function joinTranscripts(parts: string[]): string {
  return parts.map(part => part.trim()).filter(Boolean).join(" ").trim();
}
function isTargetValid(target: LockedDictationTarget): boolean {
  return target.isValid ? target.isValid() : true;
}
export function useDictation(options: {
  isEligible: () => boolean;
  lockTarget: () => LockedDictationTarget | null;
  isUnavailable?: boolean;
  enableShortcuts?: boolean;
  mode?: DictationMode;
  onDragStart?: () => void;
  onDragMove?: (deltaX: number, deltaY: number) => void;
  onDragEnd?: () => void;
}) {
  const {
    isEligible,
    lockTarget,
    isUnavailable = false,
    enableShortcuts = true,
    onDragStart,
    onDragMove,
    onDragEnd
  } = options;
  const savedMode = useDictationStore(state => state.mode);
  const mode = options.mode ?? savedMode;
  const hydrateMode = useDictationStore(state_0 => state_0.hydrate);
  const [status, setStatus] = useState<DictationStatus>("idle");
  const [listeningMode, setListeningMode] = useState<DictationListeningMode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{
    original: string;
    undo: (() => void) | null;
    copyOriginal: (() => void) | null;
    inserted: boolean;
  } | null>(null);
  const sessionRef = useRef<Session | null>(null);
  const shortcutRef = useRef<ShortcutState | null>(null);
  const keyboardThresholdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pointerThresholdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pointerTargetRef = useRef<LockedDictationTarget | null>(null);
  const pointerGestureRef = useRef<PointerGesture | null>(null);
  const keyboardHoldKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (options.mode === undefined) hydrateMode();
  }, [hydrateMode, options.mode]);
  const isLiveSession = useCallback((session: Session) => {
    return !session.isCancelled && !session.isTerminal && sessionRef.current === session && isCurrentDictationLease(session.id);
  }, []);
  const cleanupSession = useCallback((session_0: Session) => {
    if (session_0.isTerminal) return;
    session_0.isTerminal = true;
    session_0.abortController.abort();
    session_0.capture?.stop();
    session_0.connection?.close();
    session_0.chunks.length = 0;
    if (session_0.finalTimer) clearTimeout(session_0.finalTimer);
    if (session_0.maxTimer) clearTimeout(session_0.maxTimer);
    if (session_0.bootstrapTimer) clearTimeout(session_0.bootstrapTimer);
    if (session_0.openTimer) clearTimeout(session_0.openTimer);
    releaseDictationLease(session_0.id);
    session_0.confirmProvider();
    if (sessionRef.current === session_0) sessionRef.current = null;
  }, []);
  const failSession = useCallback((session_1: Session, message: string) => {
    if (!isLiveSession(session_1)) return;
    session_1.tracking.failed("session_failed");
    const original = joinTranscripts(session_1.transcripts);
    cleanupSession(session_1);
    setError(message);
    setFeedback(original ? {
      original,
      undo: null,
      inserted: false,
      copyOriginal: () => {
        void (async () => {
          try {
            if (navigator.clipboard?.writeText) {
              await navigator.clipboard.writeText(original);
            } else {
              const textarea = document.createElement("textarea");
              try {
                textarea.value = original;
                textarea.setAttribute("readonly", "");
                textarea.className = "sr-only";
                document.body.append(textarea);
                textarea.select();
                if (!document.execCommand("copy")) throw new Error("copy");
              } finally {
                textarea.remove();
              }
            }
            setError("Original transcript copied.");
          } catch {
            setError("Copy failed. Select the original transcript below.");
          }
        })();
      }
    } : null);
    setStatus("error");
    setListeningMode(null);
    session_1.target.restoreFocus();
  }, [cleanupSession, isLiveSession]);
  const insertFinal = useCallback(async (session_2: Session) => {
    if (!isLiveSession(session_2) || session_2.isFinalizing || !isTargetValid(session_2.target)) {
      if (isLiveSession(session_2) && !isTargetValid(session_2.target)) {
        failSession(session_2, "The target changed while you were speaking. Review the original and insert it when ready.");
      }
      return;
    }
    session_2.isFinalizing = true;
    setStatus("processing");
    setListeningMode(null);
    const original_0 = joinTranscripts(session_2.transcripts);
    if (!original_0) {
      session_2.tracking.failed("no_speech");
      cleanupSession(session_2);
      setStatus("idle");
      setListeningMode(null);
      session_2.target.restoreFocus();
      return;
    }
    let text = original_0;
    if (session_2.mode === "organize") {
      let result: {
        text: string;
      } | {
        error: string;
      };
      try {
        result = await Promise.race([(async () => {
          const {
            organizeDictation
          } = await loadDictationOrganizer();
          return organizeDictation(original_0);
        })(), new Promise<never>((_, reject) => setTimeout(() => reject(new Error("ORGANIZE_TIMEOUT")), ORGANIZE_TIMEOUT_MS))]);
      } catch {
        if (isLiveSession(session_2)) {
          failSession(session_2, "Could not organize this dictation. Your original is still available.");
        }
        return;
      }
      if (!isLiveSession(session_2)) return;
      if ("error" in result) {
        failSession(session_2, result.error);
        return;
      }
      text = result.text;
    }
    if (!isLiveSession(session_2) || !isTargetValid(session_2.target)) {
      failSession(session_2, "The target changed while you were speaking. Review the original and insert it when ready.");
      return;
    }
    let undo: (() => void) | null = null;
    try {
      undo = session_2.target.insert(text) ?? null;
    } catch {
      failSession(session_2, "Could not insert the text safely. Your original transcript is available below.");
      return;
    }
    session_2.tracking.completed(session_2.mode === "organize" ? "organized_text_inserted" : "transcript_inserted");
    cleanupSession(session_2);
    setFeedback({
      original: original_0,
      undo,
      copyOriginal: null,
      inserted: true
    });
    setStatus("idle");
    setListeningMode(null);
    session_2.target.restoreFocus();
  }, [cleanupSession, failSession, isLiveSession]);
  const requestFinish = useCallback((session_3: Session) => {
    if (!isLiveSession(session_3) || session_3.wantsFinish) return;
    session_3.wantsFinish = true;
    session_3.capture?.stop();
    setStatus("processing");
    setListeningMode(null);
    if (!session_3.isOpen || !session_3.connection) return;
    for (const chunk of session_3.chunks.splice(0)) {
      try {
        session_3.connection.send({
          audioBase64: chunk
        });
      } catch {
        failSession(session_3, "The transcription connection closed unexpectedly.");
        return;
      }
    }
    try {
      session_3.connection.commit();
    } catch {
      failSession(session_3, "The transcription could not be finalized.");
      return;
    }
    session_3.finalTimer = setTimeout(() => void insertFinal(session_3), FINAL_TRANSCRIPT_TIMEOUT_MS);
  }, [failSession, insertFinal, isLiveSession]);
  const cancelRecording = useCallback((restoreFocus: boolean) => {
    const session_4 = sessionRef.current;
    if (!session_4) return;
    session_4.tracking.failed("cancelled");
    session_4.isCancelled = true;
    cleanupSession(session_4);
    setStatus("idle");
    setListeningMode(null);
    setError(null);
    if (restoreFocus) session_4.target.restoreFocus();
  }, [cleanupSession]);
  const cancel = useCallback(() => cancelRecording(true), [cancelRecording]);
  const start = useCallback(async (lockedTarget?: LockedDictationTarget | null, deferProvider = false) => {
    if (isUnavailable || sessionRef.current) return;
    const target = lockedTarget ?? lockTarget();
    if (!target) return;
    setError(null);
    setFeedback(null);
    setStatus("listening");
    setListeningMode(deferProvider ? "pending" : "held");
    let confirmProvider!: () => void;
    const providerReady = new Promise<void>(resolve => {
      confirmProvider = resolve;
    });
    const session_5: Session = {
      id: 0,
      mode,
      target,
      chunks: [],
      transcripts: [],
      capture: null,
      connection: null,
      isOpen: false,
      isCancelled: false,
      isFinalizing: false,
      wantsFinish: false,
      finalTimer: null,
      maxTimer: null,
      bootstrapTimer: null,
      openTimer: null,
      abortController: new AbortController(),
      isTerminal: false,
      isProviderConfirmed: !deferProvider,
      providerReady,
      confirmProvider,
      tracking: startAuthorToolTracking({
        toolKey: "dictation",
        surface: "voice_studio"
      })
    };
    if (!deferProvider) session_5.confirmProvider();
    const leaseId = acquireDictationLease();
    if (leaseId === null) {
      session_5.tracking.failed("already_active");
      setStatus("idle");
      setListeningMode(null);
      target.restoreFocus();
      return;
    }
    session_5.id = leaseId;
    sessionRef.current = session_5;
    session_5.bootstrapTimer = setTimeout(() => failSession(session_5, "Dictation took too long to start. Try again."), BOOTSTRAP_TIMEOUT_MS);
    try {
      // Start microphone acquisition first. Token/network work happens
      // concurrently and early PCM stays only in this session's memory.
      const capturePromise = startPcmCapture(chunk_0 => {
        if (!isLiveSession(session_5) || session_5.wantsFinish) return;
        if (session_5.isOpen && session_5.connection) {
          try {
            session_5.connection.send({
              audioBase64: chunk_0
            });
          } catch {
            failSession(session_5, "The transcription connection closed unexpectedly.");
          }
        } else {
          session_5.chunks.push(chunk_0);
        }
      });
      const tokenPromise = session_5.providerReady.then(async () => {
        if (!isLiveSession(session_5)) throw new DOMException("Aborted", "AbortError");
        const response = await fetch("/api/dictation/token", {
          method: "POST",
          cache: "no-store",
          signal: session_5.abortController.signal
        });
        const payload = (await response.json()) as {
          token?: string;
          error?: string;
          maxSessionSeconds?: number;
        };
        if (!response.ok || !payload.token) {
          throw new Error(payload.error ?? "Could not start dictation.");
        }
        return {
          token: payload.token,
          maxSessionSeconds: payload.maxSessionSeconds
        };
      });
      capturePromise.then(capture => {
        if (!isLiveSession(session_5)) {
          capture.stop();
          return;
        }
        session_5.capture = capture;
        if (session_5.wantsFinish) capture.stop();
      }).catch(() => undefined);
      const [capture_0, credential] = await Promise.all([capturePromise, tokenPromise]);
      if (!isLiveSession(session_5)) {
        capture_0.stop();
        return;
      }
      session_5.capture = capture_0;
      if (session_5.bootstrapTimer) clearTimeout(session_5.bootstrapTimer);
      const connection = Scribe.connect({
        token: credential.token,
        modelId: "scribe_v2_realtime",
        audioFormat: AudioFormat.PCM_16000,
        sampleRate: 16_000,
        commitStrategy: CommitStrategy.MANUAL,
        noVerbatim: true
      });
      if (!isLiveSession(session_5)) {
        connection.close();
        return;
      }
      session_5.connection = connection;
      session_5.openTimer = setTimeout(() => failSession(session_5, "The transcription connection did not open. Try again."), OPEN_TIMEOUT_MS);
      connection.on(RealtimeEvents.OPEN, () => {
        if (!isLiveSession(session_5)) return;
        if (session_5.openTimer) clearTimeout(session_5.openTimer);
        session_5.isOpen = true;
        for (const chunk_1 of session_5.chunks.splice(0)) {
          try {
            connection.send({
              audioBase64: chunk_1
            });
          } catch {
            failSession(session_5, "The transcription connection closed unexpectedly.");
            return;
          }
        }
        if (session_5.wantsFinish) {
          session_5.wantsFinish = false;
          requestFinish(session_5);
        }
      });
      connection.on(RealtimeEvents.COMMITTED_TRANSCRIPT, data => {
        if (!isLiveSession(session_5)) return;
        if (data.text.trim()) session_5.transcripts.push(data.text);
        if (session_5.wantsFinish) void insertFinal(session_5);
      });
      connection.on(RealtimeEvents.ERROR, data_0 => {
        failSession(session_5, data_0.error || "Dictation failed. Try again.");
      });
      connection.on(RealtimeEvents.CLOSE, () => {
        if (isLiveSession(session_5)) {
          failSession(session_5, "The transcription connection closed unexpectedly.");
        }
      });
      session_5.maxTimer = setTimeout(() => requestFinish(session_5), Math.min(credential.maxSessionSeconds ?? MAX_DICTATION_SESSION_SECONDS, MAX_DICTATION_SESSION_SECONDS) * 1000);
    } catch (cause) {
      if (!isLiveSession(session_5)) return;
      failSession(session_5, cause instanceof Error && cause.name !== "AbortError" ? cause.message : "Could not start dictation.");
    }
  }, [failSession, insertFinal, isLiveSession, isUnavailable, lockTarget, mode, requestFinish]);
  const confirmSession = useCallback((sessionId: number, nextMode: DictationListeningMode) => {
    const session_6 = sessionRef.current;
    if (!session_6 || session_6.id !== sessionId || !isLiveSession(session_6)) return;
    if (!session_6.isProviderConfirmed) {
      session_6.isProviderConfirmed = true;
      session_6.confirmProvider();
    }
    setListeningMode(nextMode);
  }, [isLiveSession]);
  const finishSession = useCallback((sessionId_0: number) => {
    const session_7 = sessionRef.current;
    if (!session_7 || session_7.id !== sessionId_0) return;
    requestFinish(session_7);
  }, [requestFinish]);
  const cancelSession = useCallback((sessionId_1: number) => {
    const session_8 = sessionRef.current;
    if (!session_8 || session_8.id !== sessionId_1) return;
    cancel();
  }, [cancel]);
  useEffect(() => {
    shortcutRef.current = createShortcutState(detectPlatform());
    const apply = (event: Parameters<typeof reduceShortcut>[1]) => {
      if (!shortcutRef.current) return;
      const result_0 = reduceShortcut(shortcutRef.current, event);
      shortcutRef.current = result_0.state;
      for (const effect of result_0.effects) {
        if (effect === "schedule") {
          if (!isEligible()) continue;
          keyboardThresholdTimerRef.current = setTimeout(() => {
            apply({
              type: "threshold",
              elapsedMs: DICTATION_HOLD_THRESHOLD_MS
            });
          }, DICTATION_HOLD_THRESHOLD_MS);
        } else if (effect === "start") {
          if (isEligible()) void start();
        } else if (effect === "finish") {
          const session_9 = sessionRef.current;
          if (session_9) requestFinish(session_9);
        } else if (effect === "cancel") cancel();
      }
    };
    const onKeyDown = (event_0: globalThis.KeyboardEvent) => event_0.key === "Escape" && sessionRef.current ? cancel() : enableShortcuts && apply({
      type: "keydown",
      key: event_0.key,
      repeat: event_0.repeat
    });
    const onKeyUp = (event_1: globalThis.KeyboardEvent) => enableShortcuts && apply({
      type: "keyup",
      key: event_1.key
    });
    const abort = () => {
      if (keyboardThresholdTimerRef.current) clearTimeout(keyboardThresholdTimerRef.current);
      if (pointerThresholdTimerRef.current) clearTimeout(pointerThresholdTimerRef.current);
      keyboardThresholdTimerRef.current = null;
      pointerThresholdTimerRef.current = null;
      pointerTargetRef.current = null;
      pointerGestureRef.current = null;
      keyboardHoldKeyRef.current = null;
      shortcutRef.current = createShortcutState(detectPlatform());
      apply({
        type: "abort"
      });
      cancel();
    };
    const visibility = () => {
      if (document.visibilityState !== "visible") abort();
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", abort);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", abort);
      document.removeEventListener("visibilitychange", visibility);
      if (keyboardThresholdTimerRef.current) clearTimeout(keyboardThresholdTimerRef.current);
      if (pointerThresholdTimerRef.current) clearTimeout(pointerThresholdTimerRef.current);
      cancelRecording(false);
    };
  }, [cancel, cancelRecording, enableShortcuts, isEligible, requestFinish, start]);
  const pointerHandlers = {
    onPointerDown: (event_2: PointerEvent) => {
      if (event_2.button !== 0 || isUnavailable) return;
      event_2.preventDefault();
      event_2.currentTarget.setPointerCapture(event_2.pointerId);
      const existingSession = sessionRef.current;
      let sessionId_2 = existingSession?.id ?? null;
      const wasListeningOnDown = Boolean(existingSession);
      if (!existingSession) {
        const target_0 = lockTarget();
        if (!target_0) return;
        void start(target_0, true);
        sessionId_2 = sessionRef.current?.id ?? null;
      }
      pointerGestureRef.current = {
        pointerId: event_2.pointerId,
        sessionId: sessionId_2,
        wasListeningOnDown,
        startedAt: Date.now(),
        startX: event_2.clientX,
        startY: event_2.clientY,
        isDragging: false
      };
      pointerThresholdTimerRef.current = setTimeout(() => {
        const gesture = pointerGestureRef.current;
        if (!gesture || gesture.pointerId !== event_2.pointerId || gesture.wasListeningOnDown || gesture.sessionId === null) return;
        confirmSession(gesture.sessionId, "held");
      }, DICTATION_HOLD_THRESHOLD_MS);
    },
    onPointerMove: (event_3: PointerEvent) => {
      const gesture_0 = pointerGestureRef.current;
      if (!gesture_0 || gesture_0.pointerId !== event_3.pointerId) return;
      const deltaX = event_3.clientX - gesture_0.startX;
      const deltaY = event_3.clientY - gesture_0.startY;
      if (!gesture_0.isDragging && Math.hypot(deltaX, deltaY) >= POINTER_DRAG_THRESHOLD_PX) {
        gesture_0.isDragging = true;
        if (pointerThresholdTimerRef.current) {
          clearTimeout(pointerThresholdTimerRef.current);
          pointerThresholdTimerRef.current = null;
        }
        if (!gesture_0.wasListeningOnDown && gesture_0.sessionId !== null) {
          cancelSession(gesture_0.sessionId);
        }
        onDragStart?.();
      }
      if (gesture_0.isDragging) onDragMove?.(deltaX, deltaY);
    },
    onPointerUp: (event_4: PointerEvent) => {
      const gesture_1 = pointerGestureRef.current;
      if (!gesture_1 || gesture_1.pointerId !== event_4.pointerId) return;
      pointerGestureRef.current = null;
      if (pointerThresholdTimerRef.current) clearTimeout(pointerThresholdTimerRef.current);
      pointerThresholdTimerRef.current = null;
      if (gesture_1.isDragging) {
        onDragEnd?.();
        return;
      }
      if (gesture_1.sessionId === null) return;
      if (gesture_1.wasListeningOnDown) {
        finishSession(gesture_1.sessionId);
        return;
      }
      const wasHeld = Date.now() - gesture_1.startedAt >= DICTATION_HOLD_THRESHOLD_MS;
      if (wasHeld) {
        confirmSession(gesture_1.sessionId, "held");
        finishSession(gesture_1.sessionId);
      } else {
        confirmSession(gesture_1.sessionId, "latched");
      }
    },
    onPointerCancel: (event_5: PointerEvent) => {
      const gesture_2 = pointerGestureRef.current;
      if (!gesture_2 || gesture_2.pointerId !== event_5.pointerId) return;
      if (pointerThresholdTimerRef.current) clearTimeout(pointerThresholdTimerRef.current);
      pointerThresholdTimerRef.current = null;
      pointerGestureRef.current = null;
      pointerTargetRef.current = null;
      if (gesture_2.isDragging) onDragEnd?.();
      if (!gesture_2.wasListeningOnDown && gesture_2.sessionId !== null) {
        cancelSession(gesture_2.sessionId);
      }
    },
    onLostPointerCapture: (event_6: PointerEvent) => {
      const gesture_3 = pointerGestureRef.current;
      if (!gesture_3 || gesture_3.pointerId !== event_6.pointerId) return;
      if (pointerThresholdTimerRef.current) clearTimeout(pointerThresholdTimerRef.current);
      pointerThresholdTimerRef.current = null;
      pointerGestureRef.current = null;
      pointerTargetRef.current = null;
      if (gesture_3.isDragging) onDragEnd?.();
      if (!gesture_3.wasListeningOnDown && gesture_3.sessionId !== null) {
        cancelSession(gesture_3.sessionId);
      }
    }
  };
  const keyboardHandlers = {
    onKeyDown: (event_7: KeyboardEvent) => {
      if (event_7.key !== " " && event_7.key !== "Enter" || event_7.repeat || isUnavailable || sessionRef.current || keyboardHoldKeyRef.current) return;
      event_7.preventDefault();
      event_7.stopPropagation();
      keyboardHoldKeyRef.current = event_7.key;
      pointerTargetRef.current = lockTarget();
      if (!pointerTargetRef.current) {
        keyboardHoldKeyRef.current = null;
        return;
      }
      keyboardThresholdTimerRef.current = setTimeout(() => void start(pointerTargetRef.current), DICTATION_HOLD_THRESHOLD_MS);
    },
    onKeyUp: (event_8: KeyboardEvent) => {
      if (keyboardHoldKeyRef.current !== event_8.key) return;
      event_8.preventDefault();
      event_8.stopPropagation();
      if (keyboardThresholdTimerRef.current) clearTimeout(keyboardThresholdTimerRef.current);
      keyboardThresholdTimerRef.current = null;
      keyboardHoldKeyRef.current = null;
      pointerTargetRef.current = null;
      const session_10 = sessionRef.current;
      if (session_10) requestFinish(session_10);
    }
  };
  const undoLast = () => {
    feedback?.undo?.();
    setFeedback(null);
  };
  return {
    status,
    listeningMode,
    error,
    feedback,
    cancel,
    undoLast,
    dismissFeedback: () => setFeedback(null),
    dismissMessages: () => {
      setError(null);
      setFeedback(null);
      if (status === "error") setStatus("idle");
    },
    startRecording: () => start(),
    stopRecording: () => {
      if (sessionRef.current) requestFinish(sessionRef.current);
    },
    pointerHandlers,
    keyboardHandlers
  };
}